import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { produce } from 'immer';
import { combineLatest, filter, map, Subscription } from 'rxjs';

import { CanvasElementType } from '@blaze-runtime/engine-domain';
import { type ElementFormState, getActiveBinding } from '@blaze-shared/elements-domain';
import {
  BlazeSchema,
  createArrayOfObjectsSchemaFromColumns,
  createArrayOfStringsSchema,
  createScalarSchema,
  DatasetRow,
  isDatasetLinkValue,
} from '@blaze-shared/schema-domain';
import { isSelectOption, useEmitTracker } from '@blaze-shared/shared-util';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { deserializeBindingInput } from '../../utils/canvas-element/bindings.utils';
import { updateOptionsFields } from '../../utils/elements/element.utils';

import { CanvasElementBaseComponent, ColumnIdOrName } from './base.component';

type InputSelectMultipleElements = Extract<CanvasElementType, 'checkbox' | 'tags'>;

@UntilDestroy()
@Component({
  template: '',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class CanvasElementBaseInputSelectMultipleComponent<
    T extends InputSelectMultipleElements,
  >
  extends CanvasElementBaseComponent<T>
  implements CanvasElementComponent<T>
{
  @Input() optionsList?: DatasetRow[] = [];
  @Input() formState?: ElementFormState;

  protected readonly outputEmitTracker = useEmitTracker();
  protected readonly dataSelected = this.getDataInput('selected');
  protected readonly dataOptionsList = this.getDataInput('optionsList');

  protected selectedValue: DatasetRow[] | string[] | string | undefined;
  protected isSourceBound?: boolean;
  protected selectionSub!: Subscription | undefined;

  @ColumnIdOrName() labelField = 'label';
  @ColumnIdOrName() valueField = 'label';
  @ColumnIdOrName() imageField = 'imageSrc';
  @ColumnIdOrName() iconField = 'icon';

  selection = new SelectionModel<any>(true, []);
  /** used when the element was converted from a single-selection one
   * ex. radio → tags */
  isSingleSelect = false;

  @Input() set selected(value: DatasetRow[] | string[] | string) {
    this.selectedValue = value;
    this.processSelection(value);
  }
  get selected(): DatasetRow[] | string[] | string | undefined {
    return this.selectedValue;
  }

  constructor() {
    super();

    combineLatest({
      elementId: this.elementId$.pipe(filter(Boolean)),
      selectedSchema: this.dataSelected.schema$,
      optionsListSchema: this.dataOptionsList.schema$,
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ selectedSchema, optionsListSchema }) => {
        this.updateSchemas(selectedSchema, optionsListSchema);
      });
  }

  protected updateSchemas(
    selectedSchema: BlazeSchema | undefined,
    optionsListSchema: BlazeSchema | undefined,
  ): void {
    if (selectedSchema && !optionsListSchema) {
      this.setEventSchemaFromDataInput('change', this.dataSelected);
      return;
    }
    if (optionsListSchema && this.bindingResults?.['optionsList']) {
      let tagsArraySchema: BlazeSchema | undefined = undefined;
      if (this.bindingResults?.['selected']) {
        if (!selectedSchema) {
          return;
        }

        const column = this.dataSelected.column;
        if (column?.link) {
          tagsArraySchema = createArrayOfObjectsSchemaFromColumns(optionsListSchema.name, [
            { columnType: 'String', dataType: 'String', name: 'rowId', isProfilePicture: false },
            { columnType: 'String', dataType: 'String', name: 'display', isProfilePicture: false },
          ]);
        } else if (column?.dataType === 'Tag') {
          tagsArraySchema = column.multipleValues
            ? createArrayOfStringsSchema(optionsListSchema.name)
            : createScalarSchema('String');
        }
      } else {
        const isFilterInput =
          getActiveBinding(this.props?.data, 'optionsList')?.dataSource?.sourceType ===
            'column-filter' ||
          getActiveBinding(this.props?.data, 'optionsList')?.dataSource?.sourceType ===
            'column-filter-ranged';
        tagsArraySchema = isFilterInput
          ? produce(optionsListSchema, (draft) => {
              draft.columns[0].multipleValues = true;
            })
          : createArrayOfObjectsSchemaFromColumns(
              optionsListSchema.name,
              optionsListSchema.columns,
            );
      }
      if (!tagsArraySchema) {
        return;
      }

      const eventSchema = this.createElementSchema('change', undefined, tagsArraySchema, undefined);
      this.setDefaultEventSchema('change', eventSchema);
      return;
    }

    this.setDefaultEventSchema('change');
  }

  protected updateSelectionModel(selection: DatasetRow[]): void {
    if (this.selectionSub) {
      this.selectionSub.unsubscribe();
      this.selectionSub = undefined;
    }
    this.selection = new SelectionModel<unknown>(!this.isSingleSelect, selection);
    this.selectionSub = this.selection.changed
      .pipe(
        untilDestroyed(this),
        map((selection) => selection.source.selected),
      )
      .subscribe((selected) => {
        this.updateOutput(selected as DatasetRow[]);
      });
    this.filterOptions();
  }

  protected processBindingOptionsField(unboundOptions: string[] | undefined): void {
    const { data } = this.props || {};

    if (!this.bindingResults?.['optionsList'] && !this.bindingResults?.['selected']) {
      this.setDefaultEventSchema('change');
    }

    if (!this.bindingResults?.['optionsList']) {
      updateOptionsFields(this);
      this.optionsList = unboundOptions?.map((value) => ({ label: value, value })) || [];

      return;
    }

    const binding = getActiveBinding(data, 'optionsList');
    if (binding && isSelectOption(binding.bindings)) {
      this.imageField = binding.bindings.imageSrc;
      this.iconField = binding.bindings.icon ?? this.iconField;
      this.applyExtraBindingFields(binding.bindings as Record<string, unknown>);
      updateOptionsFields(this, binding.bindings.value as string, binding.bindings.label);
      return;
    }

    updateOptionsFields(this);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected applyExtraBindingFields(_bindings: Record<string, unknown>): void {}

  protected processSelection(newSelection: DatasetRow[] | string[] | string): void {
    let selected = deserializeBindingInput(
      this.props,
      newSelection,
      this.optionsList,
      this.valueField,
    );
    if (Array.isArray(selected) && selected.length) {
      if (isDatasetLinkValue(selected[0])) {
        selected = selected.map((s) => (isDatasetLinkValue(s) ? s.rowId : ''));
      }
    } else {
      if (isDatasetLinkValue(selected)) {
        selected = selected.rowId;
      }
    }

    this.selectedValue = selected;
  }

  protected abstract filterOptions(): void;
  protected abstract updateOutput(data?: any[] | null): void;
}
